/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import AgencyAdminFeedbackEntity from 'Models/Security/Acl/AgencyAdminFeedbackEntity';
import AgencyPlaylisterFeedbackEntity from 'Models/Security/Acl/AgencyPlaylisterFeedbackEntity';
import VisitorsFeedbackEntity from 'Models/Security/Acl/VisitorsFeedbackEntity';
import MustardManagementFeedbackEntity from 'Models/Security/Acl/MustardManagementFeedbackEntity';
import ApplicationUserFeedbackEntity from 'Models/Security/Acl/ApplicationUserFeedbackEntity';
import MustardAdminFeedbackEntity from 'Models/Security/Acl/MustardAdminFeedbackEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IFeedbackEntityAttributes extends IModelAttributes {
	liked: boolean;
	disliked: boolean;
	disabled: boolean;

	feedbackById?: string;
	feedbackBy?: Models.ApplicationUserEntity | Models.IApplicationUserEntityAttributes;
	moodTrackId?: string;
	moodTrack?: Models.MoodTrackEntity | Models.IMoodTrackEntityAttributes;
	zoneId?: string;
	zone?: Models.ZoneEntity | Models.IZoneEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('FeedbackEntity', 'Feedback')
// % protected region % [Customise your entity metadata here] end
export default class FeedbackEntity extends Model
	implements IFeedbackEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new AgencyAdminFeedbackEntity(),
		new AgencyPlaylisterFeedbackEntity(),
		new VisitorsFeedbackEntity(),
		new MustardManagementFeedbackEntity(),
		new ApplicationUserFeedbackEntity(),
		new MustardAdminFeedbackEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Liked'] off begin
	@observable
	@attribute<FeedbackEntity, boolean>()
	@CRUD({
		name: 'Liked',
		displayType: 'checkbox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public liked: boolean;
	// % protected region % [Modify props to the crud options here for attribute 'Liked'] end

	// % protected region % [Modify props to the crud options here for attribute 'Disliked'] off begin
	@observable
	@attribute<FeedbackEntity, boolean>()
	@CRUD({
		name: 'Disliked',
		displayType: 'checkbox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public disliked: boolean;
	// % protected region % [Modify props to the crud options here for attribute 'Disliked'] end

	// % protected region % [Modify props to the crud options here for attribute 'Disabled'] off begin
	@observable
	@attribute<FeedbackEntity, boolean>()
	@CRUD({
		name: 'Disabled',
		displayType: 'checkbox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public disabled: boolean;
	// % protected region % [Modify props to the crud options here for attribute 'Disabled'] end

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Feedback By'] off begin
		name: 'Feedback By',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.ApplicationUserEntity,
		// % protected region % [Modify props to the crud options here for reference 'Feedback By'] end
	})
	public feedbackById?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public feedbackBy: Models.ApplicationUserEntity;

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Mood Track'] off begin
		name: 'Mood Track',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.MoodTrackEntity,
		// % protected region % [Modify props to the crud options here for reference 'Mood Track'] end
	})
	public moodTrackId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public moodTrack: Models.MoodTrackEntity;

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Zone'] off begin
		name: 'Zone',
		displayType: 'reference-combobox',
		order: 60,
		referenceTypeFunc: () => Models.ZoneEntity,
		// % protected region % [Modify props to the crud options here for reference 'Zone'] end
	})
	public zoneId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public zone: Models.ZoneEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IFeedbackEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IFeedbackEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.liked !== undefined) {
				this.liked = attributes.liked;
			}
			if (attributes.disliked !== undefined) {
				this.disliked = attributes.disliked;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.feedbackById !== undefined) {
				this.feedbackById = attributes.feedbackById;
			}
			if (attributes.feedbackBy !== undefined) {
				if (attributes.feedbackBy === null) {
					this.feedbackBy = attributes.feedbackBy;
				} else if (attributes.feedbackBy instanceof Models.ApplicationUserEntity) {
					this.feedbackBy = attributes.feedbackBy;
					this.feedbackById = attributes.feedbackBy.id;
				} else {
					this.feedbackBy = new Models.ApplicationUserEntity(attributes.feedbackBy);
					this.feedbackById = this.feedbackBy.id;
				}
			}
			if (attributes.moodTrackId !== undefined) {
				this.moodTrackId = attributes.moodTrackId;
			}
			if (attributes.moodTrack !== undefined) {
				if (attributes.moodTrack === null) {
					this.moodTrack = attributes.moodTrack;
				} else if (attributes.moodTrack instanceof Models.MoodTrackEntity) {
					this.moodTrack = attributes.moodTrack;
					this.moodTrackId = attributes.moodTrack.id;
				} else {
					this.moodTrack = new Models.MoodTrackEntity(attributes.moodTrack);
					this.moodTrackId = this.moodTrack.id;
				}
			}
			if (attributes.zoneId !== undefined) {
				this.zoneId = attributes.zoneId;
			}
			if (attributes.zone !== undefined) {
				if (attributes.zone === null) {
					this.zone = attributes.zone;
				} else if (attributes.zone instanceof Models.ZoneEntity) {
					this.zone = attributes.zone;
					this.zoneId = attributes.zone.id;
				} else {
					this.zone = new Models.ZoneEntity(attributes.zone);
					this.zoneId = this.zone.id;
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		feedbackBy {
			${Models.ApplicationUserEntity.getAttributes().join('\n')}
			${Models.ApplicationUserEntity.getFiles().map(f => f.name).join('\n')}
		}
		moodTrack {
			${Models.MoodTrackEntity.getAttributes().join('\n')}
		}
		zone {
			${Models.ZoneEntity.getAttributes().join('\n')}
			${Models.ZoneEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return "feedback by: " + this.feedbackBy;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(FeedbackEntity.prototype, 'created');
CRUD(modifiedAttr)(FeedbackEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
